import { isMove } from '../config/moveLibrary';
import { API_BASE } from '../config';
import { micWords } from './stageBridge';

// stagePlan — where a performer stands on the plaza stage, and what they do, at
// any moment of their set (2026-11-04).
//
// Khari: "give the artists a floorplan and the analysis of their songs so they
// can plan out a complete performance. this would be a timed delivery of
// animations they think should happen. For example rapping for 30s, move to
// front of stage, wave hands during chorus, etc.. waving at fans when the show
// is over."
//
// THE FLOORPLAN is the API's (city/cityPlaces, the plaza's `stage.marks`): named
// marks in the stage's own frame. THE PLAN is per song, steps
// `[{ s, mark, move, gait }]` (ms from the song's start; each holds until the
// next). `gait` is how they get to the step's mark: 'stage' (their own stage
// walk, the default) or 'walk'; the artist's choice (Khari, 2026-11-04: "be
// sure to use their stage walk when they are moving to new locations on the
// stage … or tell them to walk.. their choice of which to use").
// A song's own plan rides on its track (`shows[].tracks[].plan`): the artist
// writes it once, from the floorplan, the song's analysis and a menu of 60 stage
// moves (API CityStageService.askStagePlan, city/stageMenu). Until a song has
// one, it is played from a default built here from its sections (a verse at
// centre, a chorus at the front, a break danced), so a set is never a statue. Around the songs, the set's bookends:
// they talk while they say their words, and wave to the fans at the end.
//
// ⚠ THE SAME ON EVERY SCREEN: read inside cityMotion.stateAt, like the rest of
// the timeline, from the show and the moment alone. A walk between marks is part
// of it: it starts with its step and takes distance / STAGE_PACE.
// ⚠ ON THE BEAT: a step's start snaps to the nearest bar of the SERVER's
// measurement of the song (GET /api/world/beat/:trackId) once it has arrived,
// so every device snaps to the same bar. Nothing is decoded here.

export const STAGE_PACE = 0.55;   // units a second between marks (~1.3 m/s: a walk, not a dash)

const TALK = 'talk_with_hands_open';
const WAVE = 'big_wave_hello';
// The recorded stage vocals that stay near their spot (≤ 70 cm of travel and
// ≤ 46° of turning, measured; vocal 03 left out, it converted 7° off), by how
// hard the song is pushing. The ones that cross the stage (up to 4.5 m) are for
// plans that walk.
const VOCAL = {
  low: ['perf_vocal_06', 'perf_vocal_07'],
  mid: ['perf_vocal_04', 'perf_vocal_05'],
  high: ['perf_vocal_10', 'perf_vocal_14'],
};
const BREAK = { low: 'joyful_dance_with_hand_sway', mid: 'joyful_dance_with_hand_sway', high: 'hip_hop_dance' };

function level(energy) {
  const e = String(energy || '').toLowerCase();
  if (/peak|high|intense|explosive/.test(e)) return 'high';
  if (/low|calm|soft|quiet|gentle|declin/.test(e)) return 'low';
  return 'mid';
}

/** A mark's world position [x, z]. +x is the fans' right; z runs toward the fans. */
export function markXZ(stage, name) {
  const m = stage?.marks?.[name] || stage?.marks?.centre || { x: 0, z: 0 };
  const f = stage?.facing || 0; const c = Math.cos(f); const s = Math.sin(f);
  return [stage.at[0] + m.x * c + m.z * s, stage.at[1] - m.x * s + m.z * c];
}

/** Is [x, z] on the stage's deck (the API's `stage.deck`: w × d, its centre at local z)? */
export function onDeck(stage, x, z) {
  const d = stage?.deck;
  if (!d || !stage.at) return false;
  const f = stage.facing || 0; const c = Math.cos(f); const s = Math.sin(f);
  const dx = x - stage.at[0]; const dz = z - stage.at[1];
  const lx = dx * c - dz * s; const lz = dx * s + dz * c;   // back into the stage's frame
  return Math.abs(lx) <= d.w / 2 && Math.abs(lz - (d.z || 0)) <= d.d / 2;
}

/** The default plan for a song with none of its own, from its sections. */
export function autoPlan(tr) {
  const secs = (tr?.feel?.sections || [])
    .filter((x) => Number.isFinite(x?.s) && x.e > x.s && x.s < (tr.durMs || Infinity))
    .sort((a, b) => a.s - b.s);
  if (!secs.length) return [{ s: 0, mark: 'centre', move: VOCAL.mid[0] }];
  const steps = []; let choruses = 0; let verses = 0;
  for (const x of secs) {
    const lv = level(x.energy); const type = String(x.type || '');
    const at = steps.length ? x.s : 0;
    let mark; let move;
    if (!x.vocals || /instrumental|interlude/.test(type)) {
      mark = 'centre'; move = BREAK[lv];
    } else if ((/chorus/.test(type) && !/pre/.test(type)) || (!type && lv === 'high')) {
      mark = ['front', 'front_left', 'front', 'front_right'][choruses % 4];
      move = VOCAL.high[choruses % 2]; choruses++;
    } else if (/intro|outro|bridge/.test(type)) {
      mark = 'centre'; move = VOCAL[lv === 'high' ? 'mid' : lv][0];
    } else {
      mark = ['centre', 'left', 'centre', 'right'][verses % 4];
      move = VOCAL[lv][verses % 2]; verses++;
    }
    steps.push({ s: at, mark, move });
  }
  return steps;
}

/** A song's own plan, kept only where it makes sense: known marks, listed moves, in order. */
export function validPlan(plan, stage, durMs) {
  if (!Array.isArray(plan) || !plan.length) return null;
  const out = [];
  for (const p of plan) {
    const s = Number(p?.s);
    if (!Number.isFinite(s) || s < 0 || s >= durMs) continue;
    const mark = stage?.marks?.[p.mark] ? p.mark : (out[out.length - 1]?.mark || 'centre');
    const move = p.move && isMove(p.move) ? p.move : null;
    out.push({ s, mark, move, gait: p.gait === 'walk' ? 'walk' : 'stage' });
  }
  out.sort((a, b) => a.s - b.s);
  if (!out.length) return null;
  out[0].s = 0;
  return out;
}

// ── The server's beat, per track (never measured here) ─────────────────────
const grids = new Map();   // trackId → { state: 'loading' | 'ready' | 'none', grid?, retryAt? }
let gridVersion = 0;       // bumps when a grid arrives, so a set's blocking is rebuilt with it
const GRID_RETRY_MS = 5 * 60000;
function gridOf(trackId) {
  const id = Number(trackId);
  if (!(id > 0) || typeof fetch !== 'function') return null;
  const e = grids.get(id);
  if (e?.state === 'ready') return e.grid;
  if (e && (e.state === 'loading' || Date.now() < (e.retryAt || 0))) return null;
  grids.set(id, { state: 'loading' });
  fetch(`${API_BASE}/api/world/beat/${id}`, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      const g = j?.grid;
      if (g && Array.isArray(g.bars) && g.bars.length) { grids.set(id, { state: 'ready', grid: g }); gridVersion++; } else grids.set(id, { state: 'none', retryAt: Date.now() + GRID_RETRY_MS });
    })
    .catch(() => grids.set(id, { state: 'none', retryAt: Date.now() + GRID_RETRY_MS }));
  while (grids.size > 48) grids.delete(grids.keys().next().value);
  return null;
}

/** A step's start, on the nearest bar (within half a bar), when the beat is known. */
function snap(ms, grid) {
  if (!grid || ms <= 0) return ms;
  let best = null;
  for (const b of grid.bars) {
    const at = b.t * 1000;
    if (best == null || Math.abs(at - ms) < Math.abs(best.at - ms)) best = { at, len: (b.len || grid.bar || 2) * 1000 };
    if (at > ms + 4000) break;
  }
  return best && Math.abs(best.at - ms) <= best.len / 2 ? Math.round(best.at) : ms;
}

// ── A whole set, blocked: every step from walking on to the last wave ───────
const blocked = new Map();   // `${a}:${t0}` → { v, steps: [{ at (ms from t0), mark, move }] }
function blockingFor(sh, stage) {
  const key = `${sh.a}:${sh.t0}`;
  const hit = blocked.get(key);
  // Rebuilt when a beat arrives, or a song's own plan does (the API sends one the
  // artist has just written, if its song has not started: CityLifeService.attachPlan).
  const v = `${gridVersion}:${(sh.tracks || []).map((tr) => tr.plan?.length || 0).join(',')}:${sh.host ? micWords(sh).length : ''}`;
  if (hit && hit.v === v) return hit.steps;
  const steps = [];
  const said = (w) => (w && Number.isFinite(w.at) ? [w.at, w.at + (Number(w.dur) || 3) * 1000] : null);
  // A HOST'S MOMENT in a booked show (API ShowBookingService, 2026-11-04): no
  // songs, only their words, at their own mark — a pair at the mic each at
  // theirs (Khari: "I would like them as a duo each time") — talking on every
  // line of theirs and still on the other's; the night's last moment ends on a wave.
  if (sh.host) {
    const home = sh.mark || 'centre';
    steps.push({ at: 0, mark: home, move: null });
    for (const [, w] of micWords(sh)) {
      const sp = said(w);
      if (sp) steps.push({ at: sp[0], mark: home, move: TALK }, { at: sp[1], mark: home, move: null });
    }
    if (sh.wave) steps.push({ at: Math.max(0, (sh.t1 - sh.t0) - 4000), mark: home, move: WAVE });
    steps.sort((a, b) => a.at - b.at);
    blocked.set(key, { v, steps });
    while (blocked.size > 8) blocked.delete(blocked.keys().next().value);
    return steps;
  }
  steps.push({ at: 0, mark: 'centre', move: null });
  const open = said(sh.open);
  if (open) steps.push({ at: open[0], mark: 'centre', move: TALK }, { at: open[1], mark: 'centre', move: null });
  const tracks = sh.tracks || [];
  let lastMark = 'centre';
  tracks.forEach((tr, i) => {
    const words = said((sh.mids || []).find((m) => m.after === i));
    if (words) { steps.push({ at: words[0], mark: 'front', move: TALK }, { at: words[1], mark: 'front', move: null }); lastMark = 'front'; }
    const at = Number(tr.at) || 0; const dur = Number(tr.durMs) || 0;
    const grid = gridOf(tr.trackId);
    const plan = validPlan(tr.plan, stage, dur) || autoPlan(tr);
    for (const p of plan) { steps.push({ at: at + snap(p.s, grid), mark: p.mark, move: p.move, gait: p.gait || 'stage' }); lastMark = p.mark; }
    steps.push({ at: at + dur, mark: lastMark, move: null });   // the last note: they take the applause
  });
  // The end of the show: wave to the fans, say their closing words, wave again.
  const last = tracks[tracks.length - 1];
  if (last) steps.push({ at: (Number(last.at) || 0) + (Number(last.durMs) || 0), mark: 'front', move: WAVE });
  const close = said(sh.close);
  if (close) steps.push({ at: close[0], mark: 'front', move: TALK }, { at: close[1], mark: 'front', move: WAVE });
  steps.sort((a, b) => a.at - b.at);
  blocked.set(key, { v, steps });
  while (blocked.size > 8) blocked.delete(blocked.keys().next().value);
  return steps;
}

// ── The band behind the lead (phase 2c, 2026-11-04) ──────────────────────────
// A band's set brings on its members who have bodies of their own (API
// CityStageService.bandOnStage: `shows[].band` = [{ a, name, mark }], at the
// backup marks). They stay on their marks, face the fans, and move to the song
// as it plays: a calmer move in a quiet section, a bigger one when it pushes,
// changing on the bar where a section starts. Until members choose their own
// moves, one in two plays an invisible guitar and the other dances (unisex moves
// only: nothing is known of how a member presents). Between songs and while the
// lead talks they stand; at the end they wave with the lead.
const BAND_KITS = [
  { low: 'perf_guitar_10', mid: 'perf_guitar_09', high: 'play_electric_guitar' },
  { low: 'happy_sway_standing', mid: 'joyful_dance_with_hand_sway', high: 'side_jumps_in_horse_stance' },
];
const bandBlocked = new Map();   // `${a}:${t0}:${member}` → { v, steps: [{ at, move }] }
function bandSteps(sh, i) {
  const key = `${sh.a}:${sh.t0}:${sh.band[i].a}`;
  const hit = bandBlocked.get(key);
  if (hit && hit.v === gridVersion) return hit.steps;
  const kit = BAND_KITS[i % BAND_KITS.length];
  const steps = [{ at: 0, move: null }];
  const tracks = sh.tracks || [];
  for (const tr of tracks) {
    const at = Number(tr.at) || 0; const dur = Number(tr.durMs) || 0;
    const grid = gridOf(tr.trackId);
    const secs = (tr.feel?.sections || []).filter((x) => Number.isFinite(x?.s) && x.e > x.s && x.s < dur).sort((p, q) => p.s - q.s);
    if (!secs.length) steps.push({ at, move: kit.mid });
    secs.forEach((x, j) => steps.push({ at: at + (j ? snap(x.s, grid) : 0), move: kit[level(x.energy)] }));
    steps.push({ at: at + dur, move: null });
  }
  const last = tracks[tracks.length - 1];
  if (last) steps.push({ at: (Number(last.at) || 0) + (Number(last.durMs) || 0), move: WAVE });
  const w = sh.close && Number.isFinite(sh.close.at) ? [sh.close.at, sh.close.at + (Number(sh.close.dur) || 3) * 1000] : null;
  if (w) steps.push({ at: w[0], move: null }, { at: w[1], move: WAVE });
  steps.sort((p, q) => p.at - q.at);
  bandBlocked.set(key, { v: gridVersion, steps });
  while (bandBlocked.size > 24) bandBlocked.delete(bandBlocked.keys().next().value);
  return steps;
}

/**
 * Where a band member of a set on this stage stands at `t`, and what they do.
 * @returns { x, z, heading, move, mark } | null (not in a set on now, or no such mark)
 */
export function bandPose(shows, avatarId, stage, t) {
  if (!stage?.at || !stage.marks) return null;
  for (const sh of shows || []) {
    if (sh.inside || !(t >= sh.t0 && t < sh.t1) || !Array.isArray(sh.band)) continue;
    const i = sh.band.findIndex((m) => Number(m.a) === Number(avatarId));
    if (i < 0 || !stage.marks[sh.band[i].mark]) continue;
    const steps = bandSteps(sh, i);
    const rel = t - sh.t0;
    let k = 0;
    while (k + 1 < steps.length && steps[k + 1].at <= rel) k++;
    const [x, z] = markXZ(stage, sh.band[i].mark);
    return { x, z, heading: stage.facing || 0, move: steps[k].move, mark: sh.band[i].mark };
  }
  return null;
}

/**
 * Where the performer of a set on this stage is at `t`, and what they are doing.
 * @returns { x, z, heading, moving, speed, move, gait } | null (no set of theirs on now)
 *   `gait` while walking: 'stage' (their stage walk) or 'walk'.
 */
export function stagePose(shows, avatarId, stage, t) {
  if (!stage?.at || !stage.marks) return null;
  const sh = (shows || []).find((x) => Number(x.a) === Number(avatarId) && !x.inside && t >= x.t0 && t < x.t1);
  if (!sh) return null;
  const steps = blockingFor(sh, stage);
  const rel = t - sh.t0;
  let k = 0;
  while (k + 1 < steps.length && steps[k + 1].at <= rel) k++;
  const cur = steps[k];
  const from = k > 0 ? steps[k - 1].mark : 'centre';
  const [ax, az] = markXZ(stage, from); const [bx, bz] = markXZ(stage, cur.mark);
  const dist = Math.hypot(bx - ax, bz - az);
  const walkMs = (dist / STAGE_PACE) * 1000;
  const since = rel - cur.at;
  if (dist > 0.05 && since < walkMs) {
    const f = Math.max(0, since) / walkMs;
    return { x: ax + (bx - ax) * f, z: az + (bz - az) * f, heading: Math.atan2(bx - ax, bz - az), moving: true, speed: STAGE_PACE, move: null, gait: cur.gait === 'walk' ? 'walk' : 'stage' };
  }
  return { x: bx, z: bz, heading: stage.facing || 0, moving: false, speed: 0, move: cur.move, gait: null };
}
